import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Car, FileSpreadsheet, LayoutGrid, List, Printer, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { downloadExcel, fmtErr } from "../api";
import { useAuth } from "../context/AuthContext";
import DataTable from "../components/DataTable";
import StatusBadge from "../components/StatusBadge";
import VehicleFormDialog from "../components/VehicleFormDialog";
import { Button } from "../components/ui/button";
import { fmtMoney, fmtNum } from "../utils/format";

const STATUSES = ["", "Commandé", "Acheté", "En transit", "Arrivé au port", "En dédouanement", "Dédouané", "En préparation", "Disponible", "En entretien", "En location", "Réservé", "Vendu"];

export default function Vehicles() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [view, setView] = useState("table");
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete = ["admin", "gestionnaire"].includes(user?.role);

  const delVehicle = async (e, v) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer définitivement ${v.brand} ${v.model} (${v.internal_number}) et tout son historique (dépenses, entretiens, documents) ?`)) return;
    try {
      await api.delete(`/vehicles/${v.id}`);
      toast.success("Véhicule supprimé");
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const load = () => {
    api.get(`/vehicles${status ? `?status=${encodeURIComponent(status)}` : ""}`).then((r) => setRows(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, [status]);

  const margin = (v) => {
    if (v.profit != null) return v.profit;
    if (v.target_sale_price && v.cost_basis) return v.target_sale_price - v.cost_basis;
    return null;
  };

  return (
    <div className="space-y-5" data-testid="vehicles-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Véhicules</h1>
          <p className="mt-1 text-sm text-slate-500">{rows.length} véhicule(s) — coût de revient calculé automatiquement.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => downloadExcel(`/export/excel?type=vehicules${status ? "" : ""}`)} data-testid="export-vehicles-excel">
            <FileSpreadsheet className="mr-1 h-4 w-4" /> Excel
          </Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="export-vehicles-pdf">
            <Printer className="mr-1 h-4 w-4" /> PDF
          </Button>
          <div className="flex rounded-lg border border-slate-200 p-0.5">
            <Button size="sm" variant={view === "table" ? "default" : "ghost"} className={view === "table" ? "bg-cyan-600 hover:bg-cyan-700" : ""} onClick={() => setView("table")} data-testid="view-table-button"><List className="h-4 w-4" /></Button>
            <Button size="sm" variant={view === "cards" ? "default" : "ghost"} className={view === "cards" ? "bg-cyan-600 hover:bg-cyan-700" : ""} onClick={() => setView("cards")} data-testid="view-cards-button"><LayoutGrid className="h-4 w-4" /></Button>
          </div>
          <VehicleFormDialog onSaved={load} />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <Button key={s || "all"} size="sm" variant={status === s ? "default" : "outline"}
            className={status === s ? "bg-cyan-600 hover:bg-cyan-700" : ""}
            onClick={() => setStatus(s)} data-testid={`filter-status-${s ? s.toLowerCase().replace(/[^a-zà-ÿ]/g, "-") : "tous"}`}>
            {s || "Tous"}
          </Button>
        ))}
      </div>

      {view === "table" ? (
        <DataTable
          testidPrefix="vehicles-table"
          rows={rows}
          searchKeys={["internal_number", "brand", "model", "vin", "plate"]}
          searchPlaceholder="Rechercher par n°, marque, VIN, immatriculation…"
          onRowClick={(r) => navigate(`/vehicules/${r.id}`)}
          columns={[
            { key: "internal_number", label: "N° interne", render: (r) => <span className="font-mono-num font-semibold">{r.internal_number}</span> },
            { key: "brand", label: "Véhicule", render: (r) => (
              <div className="flex items-center gap-3">
                {r.main_photo ? (
                  <img src={r.main_photo} alt="" className="h-10 w-14 rounded object-cover" />
                ) : (
                  <div className="flex h-10 w-14 items-center justify-center rounded bg-slate-100"><Car className="h-5 w-5 text-slate-400" /></div>
                )}
                <div>
                  <p className="font-semibold text-slate-800">{r.brand} {r.model}</p>
                  <p className="text-xs text-slate-400">{r.year} · {r.fuel_type} · {fmtNum(r.mileage)} km</p>
                </div>
              </div>
            ) },
            { key: "vin", label: "VIN", render: (r) => <span className="font-mono-num text-xs">{r.vin || "—"}</span> },
            { key: "status", label: "Statut", render: (r) => <StatusBadge status={r.status} /> },
            { key: "days_in_stock", label: "Jours en stock", className: "text-center", render: (r) =>
              r.days_in_stock != null ? (
                <span className={`font-mono-num font-semibold ${r.days_in_stock > 90 ? "text-red-600" : r.days_in_stock > 60 ? "text-amber-600" : r.days_in_stock > 30 ? "text-sky-600" : "text-slate-600"}`}>{r.days_in_stock} j</span>
              ) : <span className="text-slate-300">—</span> },
            { key: "purchase_price_fcfa", label: "Prix d'achat", className: "text-right", render: (r) => (
              <div className="text-right">
                <p className="font-mono-num font-medium">{fmtMoney(r.purchase_price_fcfa)}</p>
                {r.purchase_currency !== "FCFA" && <p className="text-xs text-slate-400">{fmtNum(r.purchase_price)} {r.purchase_currency}</p>}
              </div>
            ) },
            { key: "cost_basis", label: "Coût de revient", className: "text-right", render: (r) => <span className="font-mono-num font-bold text-slate-900">{fmtMoney(r.cost_basis)}</span> },
            { key: "sale_price", label: "Prix de vente", className: "text-right", render: (r) => <span className="font-mono-num">{fmtMoney(r.sale_price ?? r.target_sale_price)}</span> },
            { key: "profit", label: "Marge", className: "text-right", render: (r) => {
              const m = margin(r);
              return m != null ? (
                <span className={`font-mono-num font-bold ${m >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtMoney(m)}{r.profit == null && <span className="block text-[10px] font-normal text-slate-400">potentielle</span>}</span>
              ) : <span className="text-slate-300">—</span>;
            } },
            ...(canDelete ? [{ key: "actions", label: "", sortable: false, render: (r) => (
              <button onClick={(e) => delVehicle(e, r)} className="text-slate-300 hover:text-red-500" data-testid={`delete-vehicle-${r.internal_number}`}><Trash2 className="h-4 w-4" /></button>
            ) }] : []),
          ]}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="vehicles-cards">
          {rows.length === 0 && <p className="col-span-full py-12 text-center text-slate-400">Aucun véhicule.</p>}
          {rows.map((v) => (
            <button key={v.id} onClick={() => navigate(`/vehicules/${v.id}`)} data-testid={`vehicle-card-${v.internal_number}`}
              className="group overflow-hidden rounded-xl border border-slate-200 bg-white text-left shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-cyan-300 hover:shadow-lg">
              <div className="relative h-44 bg-slate-100">
                {v.main_photo ? (
                  <img src={v.main_photo} alt="" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                ) : (
                  <div className="flex h-full items-center justify-center"><Car className="h-12 w-12 text-slate-300" /></div>
                )}
                <div className="absolute left-3 top-3"><StatusBadge status={v.status} /></div>
                {v.days_in_stock != null && (
                  <span className="absolute right-3 top-3 rounded-full bg-black/60 px-2.5 py-0.5 text-[11px] font-bold text-white backdrop-blur">{v.days_in_stock} j en stock</span>
                )}
              </div>
              <div className="p-4">
                <p className="font-heading font-bold text-slate-900">{v.brand} {v.model} <span className="font-normal text-slate-400">{v.year}</span></p>
                <p className="font-mono-num mt-0.5 text-xs text-slate-400">{v.internal_number} · {v.vin || "VIN —"} · {fmtNum(v.mileage)} km</p>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-slate-50 py-1.5"><p className="text-[10px] uppercase text-slate-400">Achat</p><p className="font-mono-num text-xs font-semibold">{fmtMoney(v.purchase_price_fcfa)}</p></div>
                  <div className="rounded-lg bg-cyan-50 py-1.5"><p className="text-[10px] uppercase text-cyan-600">Revient</p><p className="font-mono-num text-xs font-bold text-cyan-700">{fmtMoney(v.cost_basis)}</p></div>
                  <div className="rounded-lg bg-emerald-50 py-1.5"><p className="text-[10px] uppercase text-emerald-600">Marge</p><p className="font-mono-num text-xs font-bold text-emerald-700">{margin(v) != null ? fmtMoney(margin(v)) : "—"}</p></div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
